import React, { useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import {
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  DropdownItem,
} from 'reactstrap';
import Button from 'components/Button';
import { ButtonPropsCombined } from 'components/Button/Button';

import './ButtonWithDropdown.scss';

export type DropdownItemType = 'item' | 'label' | 'separator';

interface DropdownItemConfig {
  text?: React.ReactNode;
  textKey?: string | number;
  onClick?: () => void;
  disabled?: boolean;
  type?: DropdownItemType;
}

interface ButtonWithDropdownProps {
  dropdownItems: DropdownItemConfig[];
  openOnHover?: boolean;
  closeOnItemClick?: boolean;
  menuAriaLabel?: string;
  dropdownAlign?: 'center' | 'left';
}

const ButtonWithDropdown: React.FC<
  ButtonPropsCombined & ButtonWithDropdownProps
> = props => {
  const {
    children,
    color,
    dropdownItems,
    openOnHover = true,
    closeOnItemClick = true,
    menuAriaLabel,
    dropdownAlign = 'left',
    disabled,
    block,
    onClick,
    ...buttonProps
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const closeTimeoutRef = useRef<number | null>(null);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = window.setTimeout(() => {
      setIsOpen(false);
    }, 150);
  }, [clearCloseTimeout]);

  useEffect(() => clearCloseTimeout, [clearCloseTimeout]);

  const toggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleButtonClick: React.MouseEventHandler<HTMLButtonElement> =
    useCallback(
      e => {
        if (onClick) {
          e.preventDefault();
          e.stopPropagation();
          onClick(e);
          return;
        }
        toggle();
      },
      [onClick, toggle]
    );

  const handleMouseEnter = useCallback(() => {
    if (!openOnHover || disabled) {
      return;
    }
    clearCloseTimeout();
    setIsOpen(true);
  }, [openOnHover, disabled, clearCloseTimeout]);

  const handleMouseLeave = useCallback(() => {
    if (!openOnHover) {
      return;
    }
    scheduleClose();
  }, [openOnHover, scheduleClose]);

  const handleItemClick = useCallback(
    (itemOnClick?: () => void) => {
      itemOnClick?.();
      if (closeOnItemClick) {
        setIsOpen(false);
      }
    },
    [closeOnItemClick]
  );

  return (
    <Dropdown
      isOpen={isOpen}
      toggle={toggle}
      className={cx('btn-with-dropdown', { 'btn-with-dropdown--block': block })}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <DropdownToggle tag="div" className="dropdown-toggle-wrapper">
        <Button
          color={color}
          {...buttonProps}
          disabled={disabled}
          block={block}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onClick={handleButtonClick}
        >
          {children}
        </Button>
      </DropdownToggle>
      <DropdownMenu
        right
        flip
        container={block ? undefined : 'body'}
        className={cx('btn-with-dropdown-menu', {
          'dropdown-align-left': dropdownAlign === 'left',
          'dropdown-align-center': dropdownAlign !== 'left',
        })}
        aria-label={menuAriaLabel}
        modifiers={{
          flip: {
            enabled: true,
            behavior: ['bottom', 'top'],
          },
          preventOverflow: {
            enabled: true,
            boundariesElement: 'viewport',
          },
        }}
      >
        {dropdownItems.map((item, index) => {
          const key = item.textKey ?? index;
          if (item.type === 'separator') {
            return <DropdownItem key={key} divider />;
          }
          if (item.type === 'label') {
            return (
              <DropdownItem key={key} header className="dropdown-label">
                {item.text}
              </DropdownItem>
            );
          }
          return (
            <DropdownItem
              key={key}
              disabled={item.disabled}
              onClick={() => handleItemClick(item.onClick)}
            >
              {item.text}
            </DropdownItem>
          );
        })}
      </DropdownMenu>
    </Dropdown>
  );
};

export default ButtonWithDropdown;
